import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { useUserRole } from '@/hooks/useUserRole';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Check, X, Loader2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useEnhancedMobile } from '@/hooks/use-enhanced-mobile';

interface BetaApplication {
  id: string;
  user_id: string | null;
  profile_id: string | null;
  status: 'pending' | 'approved' | 'rejected';
  applied_at: string;
  reviewed_at: string | null;
  admin_notes: string | null;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  account_type_interest: string | null;
  application_reason: string | null;
  referral_source: string | null;
  email: string | null;
  display_name: string | null;
  invitation_sent_at: string | null;
  profile: {
    email: string;
    display_name: string;
    account_type: string | null;
  };
}

const AdminBetaApplicationsPage = () => {
  const navigate = useNavigate();
  const { loading: authLoading } = useSupabaseAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [activeTab, setActiveTab] = useState('pending');
  const queryClient = useQueryClient();
  const { isMobile } = useEnhancedMobile();

  // Refetch data when switching tabs to ensure fresh data
  useEffect(() => {
    queryClient.invalidateQueries({ 
      queryKey: ['beta-applications', activeTab],
      refetchType: 'active'
    });
  }, [activeTab, queryClient]);

  const { data: applications, isLoading } = useQuery({
    queryKey: ['beta-applications', activeTab],
    queryFn: async () => {
      // First, get all profiles that have completed setup
      // Only show applications for users who have finished their profile setup
      const { data: completedProfiles, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('setup_completed', true);

      if (profileError) throw profileError;

      const completedProfileIds = completedProfiles?.map(p => p.id) || [];

      // If no completed profiles, return empty array
      if (completedProfileIds.length === 0) {
        return [] as BetaApplication[];
      }

      let query = supabase
        .from('beta_applications')
        .select('*')
        .in('profile_id', completedProfileIds) // Only show applications with completed profiles
        .order('applied_at', { ascending: false });

      if (activeTab !== 'all') {
        query = query.eq('status', activeTab);
      }

      const { data: apps, error } = await query;
      if (error) throw error;

      // Fetch profile data for applications with profile_id
      const appsWithProfiles = await Promise.all(
        (apps || []).map(async (app) => {
          if (app.profile_id) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('email, display_name, account_type')
              .eq('id', app.profile_id)
              .single();

            // Get fallback display name from talent/brand/agent/venture tables
            let displayName = profile?.display_name;
            if (!displayName && profile?.account_type && app.profile_id) {
              if (profile.account_type === 'talent') {
                const { data: talent } = await supabase
                  .from('talents')
                  .select('name')
                  .eq('profile_id', app.profile_id)
                  .single();
                displayName = talent?.name || '';
              } else if (profile.account_type === 'brand') {
                const { data: brand } = await supabase
                  .from('brands')
                  .select('company_name')
                  .eq('profile_id', app.profile_id)
                  .single();
                displayName = brand?.company_name || '';
              } else if (profile.account_type === 'agent') {
                const { data: agent } = await supabase
                  .from('agents')
                  .select('professional_name')
                  .eq('profile_id', app.profile_id)
                  .single();
                displayName = agent?.professional_name || '';
              } else if (profile.account_type === 'venture') {
                const { data: venture } = await supabase
                  .from('venture_capitals')
                  .select('firm_name')
                  .eq('profile_id', app.profile_id)
                  .single();
                displayName = venture?.firm_name || '';
              }
            }

            return {
              ...app,
              profile: {
                email: profile?.email || app.email || '',
                display_name: displayName || profile?.display_name || app.display_name || '',
                account_type: profile?.account_type || app.account_type_interest
              }
            };
          } else {
            // No profile - skip (shouldn't happen with the filter above)
            return {
              ...app,
              profile: {
                email: app.email || '',
                display_name: app.display_name || '',
                account_type: app.account_type_interest
              }
            };
          }
        })
      );

      return appsWithProfiles as BetaApplication[];
    },
    enabled: isAdmin && !authLoading,
  });

  const approveMutation = useMutation({
    mutationFn: async (applicationId: string) => {
      const app = applications?.find(a => a.id === applicationId);
      if (!app) throw new Error('Application not found');
      if (!app.profile_id) throw new Error('No profile associated with this application');

      // 1. Update beta_applications status
      const { error: appError } = await supabase
        .from('beta_applications')
        .update({
          status: 'approved',
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', applicationId);

      if (appError) throw appError;

      // 2. Grant beta access on profile
      const updateData: any = { beta_access: true };
      if (app.account_type_interest) {
        updateData.account_type = app.account_type_interest;
      }
      
      const { error: profileError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', app.profile_id);

      if (profileError) throw profileError;

      // 3. Send welcome email - prioritize profile email over application email
      const emailToSend = app.profile?.email || app.email;
      const displayNameToSend = app.profile?.display_name || app.display_name || 'User';

      if (emailToSend) {
        const { error: emailError } = await supabase.functions.invoke(
          'send-welcome-email',
          {
            body: {
              email: emailToSend,
              displayName: displayNameToSend,
              accountType: app.account_type_interest || app.profile?.account_type,
            },
          }
        );

        if (emailError) {
          console.error('Failed to send welcome email:', emailError);
          // Don't throw - user is still approved
        }
      } else {
        console.warn('No email found for application, skipping welcome email');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['beta-applications'],
        refetchType: 'all'
      });
      toast({
        title: 'Application Approved',
        description: 'User can now log in with their credentials.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to approve application: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (applicationId: string) => {
      const { error } = await supabase
        .from('beta_applications')
        .update({
          status: 'rejected',
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', applicationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['beta-applications'],
        refetchType: 'all'
      });
      toast({
        title: 'Application Rejected',
        description: 'Application has been rejected.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to reject application: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  if (authLoading || roleLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <Layout>
      <div className="container mx-auto py-8 px-4 max-w-[1200px]">
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Card>
          <CardHeader>
            <CardTitle>Beta Applications</CardTitle>
            <CardDescription>
              Review and manage beta access requests
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="pending">Pending</TabsTrigger>
                <TabsTrigger value="approved">Approved</TabsTrigger>
                <TabsTrigger value="rejected">Rejected</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab}>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : !applications || applications.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No applications found
                  </div>
                ) : (
                  <>
                    {/* Desktop Table View */}
                    <div className="hidden md:block">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Account Type</TableHead>
                            <TableHead>Applied</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {applications.map((app) => (
                            <TableRow key={app.id}>
                              <TableCell className="font-medium">
                                {app.profile.email}
                              </TableCell>
                              <TableCell>{app.profile.display_name}</TableCell>
                              <TableCell>
                                {app.profile.account_type ? (
                                  <Badge variant="outline">
                                    {app.profile.account_type}
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground text-sm">
                                    Not set
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                {format(new Date(app.applied_at), 'MMM d, yyyy')}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    app.status === 'approved'
                                      ? 'default'
                                      : app.status === 'rejected'
                                      ? 'destructive'
                                      : 'secondary'
                                  }
                                >
                                  {app.status}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {app.status === 'pending' && (
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() => approveMutation.mutate(app.id)}
                                      disabled={approveMutation.isPending}
                                    >
                                      {approveMutation.isPending ? (
                                        <>
                                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                          Pending...
                                        </>
                                      ) : (
                                        <>
                                          <Check className="w-4 h-4 mr-1" />
                                          Approve
                                        </>
                                      )}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => rejectMutation.mutate(app.id)}
                                      disabled={rejectMutation.isPending}
                                    >
                                      <X className="w-4 h-4 mr-1" />
                                      Reject
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-4">
                      {applications.map((app) => (
                        <Card key={app.id} className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-sm truncate">
                                  {app.profile.display_name}
                                </h3>
                                <p className="text-xs text-muted-foreground truncate">
                                  {app.profile.email}
                                </p>
                              </div>
                              <Badge
                                variant={
                                  app.status === 'approved'
                                    ? 'default'
                                    : app.status === 'rejected'
                                    ? 'destructive'
                                    : 'secondary'
                                }
                              >
                                {app.status}
                              </Badge>
                            </div>

                            <div className="flex items-center gap-2 text-xs">
                              {app.profile.account_type ? (
                                <Badge variant="outline" className="text-xs">
                                  {app.profile.account_type}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">
                                  No account type
                                </span>
                              )}
                              <span className="text-muted-foreground">
                                • Applied {format(new Date(app.applied_at), 'MMM d, yyyy')}
                              </span>
                            </div>

                            {app.status === 'pending' && (
                              <div className="flex items-center gap-2 pt-2">
                                <Button
                                  size="sm"
                                  className="flex-1 min-h-[44px]"
                                  onClick={() => approveMutation.mutate(app.id)}
                                  disabled={approveMutation.isPending}
                                >
                                  {approveMutation.isPending ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      Pending...
                                    </>
                                  ) : (
                                    <>
                                      <Check className="w-4 h-4 mr-2" />
                                      Approve
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1 min-h-[44px]"
                                  onClick={() => rejectMutation.mutate(app.id)}
                                  disabled={rejectMutation.isPending}
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminBetaApplicationsPage;
